/**
 * The Fleet tab's dashboard sections (dashboard spec §2.2 "4 · Fleet", §5.8; W3 card M2): Ships (the Fleet screen,
 * where U lands), Captains, Feed and Berths. Each feature registers its section with `fleetSection` and renders its
 * existing inner component inside the dashboard shell, which owns the frame, Close, the hint line, the modal and the
 * keys. The shell hands focus inputs to the open section (`DashNavPort`); these hooks wire them and keep the section
 * live (re-render on change, a refresh clock while mounted). UI events only.
 */
import { useEffect, useReducer, useRef, type RefObject } from 'react'
import { z } from 'zod'
import type { DashFocusInput, DashNavPort } from '../dashboard/focus'
import type { DashContext, DashSection, DashSectionComponent } from '../dashboard/registry'
import { navigateButtons, type NavInput } from '../strategic/mount'
import type { FleetAction } from './fleetModel'
import sectionsJson from './fleetSections.json'

const entry = z.object({ label: z.string().min(1), order: z.number() })
const sectionsSchema = z.object({ ships: entry, captains: entry, feed: entry, berths: entry })
const SECTIONS = sectionsSchema.parse(sectionsJson)

export type FleetSectionKey = 'ships' | 'captains' | 'feed' | 'berths'

export const FLEET_SECTION_IDS: Readonly<Record<FleetSectionKey, string>> = Object.freeze({
  ships: 'fleet.ships',
  captains: 'fleet.captains',
  feed: 'fleet.feed',
  berths: 'fleet.berths',
})

/** One Fleet tab section (career in the Reach only; outside it the shell shows its empty state). */
export function fleetSection(key: FleetSectionKey, component: DashSectionComponent): DashSection {
  return {
    id: FLEET_SECTION_IDS[key],
    tab: 'fleet',
    order: SECTIONS[key].order,
    label: SECTIONS[key].label,
    ...(key === 'ships' ? { deepLink: 'fleetScreen' as const } : {}),
    available: (ctx: DashContext) => ctx.career,
    component,
  }
}

/**
 * A shell focus input → a Fleet screen action. ←/→ move between the list and the control bar (the bar's controls
 * in turn), replacing the old in-panel Tab; Enter/Space select (activate), F opens the ship, PgUp/PgDn page.
 */
export function fleetInputAction(i: DashFocusInput): FleetAction | null {
  switch (i) {
    case 'pageUp':
      return { type: 'page', dir: -1 }
    case 'pageDown':
      return { type: 'page', dir: 1 }
    case 'secondary':
      return { type: 'open' }
    case 'context':
      return null
    default:
      return { type: 'nav', input: i }
  }
}

/** A shell focus input → a button-list input (`navigateButtons`); paging, F and R are not button moves. */
export function toNavInput(i: DashFocusInput): NavInput | null {
  switch (i) {
    case 'up':
    case 'down':
    case 'left':
    case 'right':
    case 'activate':
      return i
    default:
      return null
  }
}

/** Re-renders on `subscribe`; while mounted, `refresh` runs now and every `refreshMs` (0: once). */
export function useLive(subscribe: (fn: () => void) => () => void, refresh?: () => unknown, refreshMs = 0): void {
  const [, bump] = useReducer((n: number) => n + 1, 0)
  useEffect(() => subscribe(bump), [subscribe])
  useEffect(() => {
    if (refresh === undefined) return undefined
    void refresh()
    if (refreshMs <= 0) return undefined
    const iv = window.setInterval(() => void refresh(), refreshMs)
    return (): void => window.clearInterval(iv)
  }, [refresh, refreshMs])
}

/** Subscribes the section to the shell's focus inputs while mounted; `handle` returns true when it used one. */
export function useNavInput(nav: DashNavPort, handle: (i: DashFocusInput) => boolean): void {
  const h = useRef(handle)
  h.current = handle
  useEffect(() => nav.subscribe((i) => h.current(i)), [nav])
}

/** A plain button list (Feed, Berths): arrows move through its enabled buttons, Enter/Space presses one. */
export function useButtonNav(nav: DashNavPort, root: RefObject<HTMLElement | null>): void {
  useNavInput(nav, (i) => {
    const n = toNavInput(i)
    const el = root.current
    return n !== null && el !== null && navigateButtons(el, n)
  })
}
